import { DroneResponseOperationalStatus } from 'generatedTypes/asset/models/droneResponseOperationalStatus';
import { useEffect, useRef, useState } from 'react';

import useDrones from 'hooks/useDrones';

import { DRONES_PAGE_SIZE } from './constants';
import { maintenancePageMocks } from './mockData';
import type {
    DroneResponse,
    FetchNextPage,
    HandleSearch,
    PaginatedGenerator,
    UseDataReturn
} from './types';

const useData = (): UseDataReturn => {
    const { fetch, isLoading, reset, totalCount } = useDrones();

    const [drones, setDrones] = useState<DroneResponse[]>([]);
    const [hasMore, setHasMore] = useState(false);

    const generatorRef = useRef<PaginatedGenerator<DroneResponse> | null>(null);
    const generationRef = useRef(0);

    const fetchData = async (searchTerm?: string) => {
        reset();

        const generation = ++generationRef.current;

        generatorRef.current = fetch({
            pageSize: DRONES_PAGE_SIZE,
            searchQuery: searchTerm || undefined
        });

        setDrones([]);

        const { done, value } = await generatorRef.current.next();

        if (generation !== generationRef.current) {
            return;
        }

        setDrones(value ?? []);
        setHasMore(!done);
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchNextPage: FetchNextPage = async () => {
        if (!generatorRef.current || !hasMore) {
            return { done: true, value: undefined };
        }

        const generation = generationRef.current;

        const result = await generatorRef.current.next();
        const { done, value } = result;

        if (generation !== generationRef.current) {
            return { done: true, value: undefined };
        }

        if (value) {
            setDrones(current => [...current, ...value]);
        }

        setHasMore(!done);

        return result;
    };

    const handleSearch: HandleSearch = async searchValue => {
        await fetchData(searchValue);
    };

    const activeDrones = drones.filter(
        drone => drone.operationalStatus === DroneResponseOperationalStatus.ACTIVE
    ).length;

    return {
        activeDrones,
        drones,
        fetchNextPage,
        groundedDrones: maintenancePageMocks.groundedDrones,
        hasMore,
        isLoading,
        onSearch: handleSearch,
        totalDrones: totalCount || drones.length
    };
};

export default useData;
